/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import {
  useLeaderboard,
  isLeaderboardEmpty,
  LeaderboardCategory,
} from '@sportninja/common/hooks/useLeaderboard'
import React from 'react'
import Button from 'src/components/ts-components/Button'
import Text from 'src/components/ts-components/Text'
import LeaderboardCard from './LeaderboardCard'
import LeadersSkeleton from './LeadersSkeleton'

interface LeadersViewProps {
  scheduleId?: string
  isGoalieStats?: boolean
  scheduleForSearch?: string
  isGlobalFilterEnabled?: boolean
  onViewMore?: (abbreviation: string) => void
}

const LeadersView: React.FC<LeadersViewProps> = ({
  scheduleId,
  isGoalieStats = false,
  scheduleForSearch,
  isGlobalFilterEnabled,
  onViewMore,
}) => {
  const { data, loading, error, refetch, isPreviousData } = useLeaderboard(
    scheduleId,
    scheduleForSearch,
    undefined, // conferenceId - not used in web yet
    isGlobalFilterEnabled,
    isGoalieStats
  )

  // Cold load, or a filter change still showing the old board: skeleton.
  // A background refetch of the board on screen keeps it visible.
  if (loading && (!data || isPreviousData)) {
    return <LeadersSkeleton />
  }

  // Error state
  if (error) {
    return (
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 300px;
          gap: 16px;
          padding: 24px;
          text-align: center;
        `}
      >
        <Text variant='paragraphSmall' color={colors.ERROR_300}>
          {error.message}
        </Text>
        <div
          css={css`
            margin-top: 16px;
            height: 48px;
          `}
        >
          <Button text='Try Again' variant='primary' onClick={refetch} />
        </div>
      </div>
    )
  }

  // Empty state
  if (!data || isLeaderboardEmpty(data)) {
    return (
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 300px;
          gap: 16px;
          padding: 24px;
          text-align: center;
        `}
      >
        <Text variant='paragraphSmall' color={colors.NEUTRAL_50}>
          No Leaderboard Data Available For This Schedule
        </Text>
        <div
          css={css`
            margin-top: 16px;
            height: 48px;
          `}
        >
          <Button text='Refresh' variant='secondary' onClick={refetch} />
        </div>
      </div>
    )
  }

  const hasPlayers = data.players.some((cat) => cat.players.length > 0)
  const hasGoalies = data.goalies.some((cat) => cat.players.length > 0)

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 24px;
      `}
    >
      {/* Players Section - only show when isGoalieStats is false */}
      {!isGoalieStats && hasPlayers && (
        <LeaderboardGrid
          categories={data.players}
          scheduleId={scheduleId}
          onViewMore={onViewMore}
        />
      )}

      {/* Goalies Section - only show when isGoalieStats is true */}
      {isGoalieStats && hasGoalies && (
        <LeaderboardGrid
          categories={data.goalies}
          scheduleId={scheduleId}
          onViewMore={onViewMore}
        />
      )}
    </div>
  )
}

interface LeaderboardGridProps {
  categories: LeaderboardCategory[]
  scheduleId?: string
  onViewMore?: (abbreviation: string) => void
}

const LeaderboardGrid: React.FC<LeaderboardGridProps> = ({
  categories,
  scheduleId,
  onViewMore,
}) => {
  // Filter out empty categories
  const nonEmptyCategories = categories.filter((cat) => cat.players.length > 0)

  if (nonEmptyCategories.length === 0) {
    return null
  }

  return (
    <div
      css={css`
        margin-bottom: 24px;
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 24px;

        @media (max-width: 1200px) {
          grid-template-columns: repeat(2, 1fr);
        }

        @media (max-width: 768px) {
          grid-template-columns: 1fr;
          padding: 0 16px;
        }
      `}
    >
      {nonEmptyCategories.map((category) => (
        <LeaderboardCard
          key={category.title}
          title={category.title}
          statId={category.statId}
          abbreviation={category.abbreviation}
          players={category.players}
          precision={category.precision}
          scheduleId={scheduleId}
          onViewMore={
            onViewMore && category.abbreviation
              ? () => onViewMore(category.abbreviation)
              : undefined
          }
        />
      ))}
    </div>
  )
}

export default LeadersView
